import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import type {
  AiAutomationSettings,
  ConnectedInbox,
  ConnectedInboxProvider,
} from '@dill/dill-shared';

/**
 * Dill AI email agent: connected Gmail / Outlook inboxes and the AI automation settings that
 * control what the agent answers. No slice — the settings section keeps this in local state.
 */

function extractErrorMessage(error: any, fallback: string): string {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((e: any) => e?.msg)
      .filter(Boolean)
      .join(' ');
  }
  return error?.message || fallback;
}

const authHeaders = async () => {
  const token = await myFirebaseAuth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return { Authorization: `Bearer ${token}` };
};

export type ConnectedInboxSummary = Omit<ConnectedInbox, 'syncCursor'>;

export const fetchConnectedInboxesThunk = createAsyncThunk(
  'emailAgent/fetchConnectedInboxes',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${supplierId}/emailAgent/inboxes`,
        { headers: await authHeaders() }
      );
      return response.data.data as {
        inboxes: ConnectedInboxSummary[];
        providers: Record<ConnectedInboxProvider, boolean>;
      };
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to load inboxes'));
    }
  }
);

export const getInboxConnectUrlThunk = createAsyncThunk(
  'emailAgent/getInboxConnectUrl',
  async (
    {
      supplierId,
      provider,
      email,
      returnPath,
    }: {
      supplierId: string;
      provider: ConnectedInboxProvider;
      email: string;
      returnPath: string;
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${supplierId}/emailAgent/inboxes/connect-url`,
        { provider, email, returnPath },
        { headers: await authHeaders() }
      );
      return response.data.data.authUrl as string;
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to start sign-in'));
    }
  }
);

export const disconnectInboxThunk = createAsyncThunk(
  'emailAgent/disconnectInbox',
  async ({ supplierId, inboxId }: { supplierId: string; inboxId: string }, thunkApi) => {
    try {
      await axios.delete(`${BASE_API_URL}/suppliers/${supplierId}/emailAgent/inboxes/${inboxId}`, {
        headers: await authHeaders(),
      });
      return inboxId;
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to disconnect inbox'));
    }
  }
);

export const updateAiAutomationSettingsThunk = createAsyncThunk(
  'emailAgent/updateAiAutomationSettings',
  async (
    {
      supplierId,
      aiAutomationSettings,
    }: { supplierId: string; aiAutomationSettings: AiAutomationSettings },
    thunkApi
  ) => {
    try {
      const response = await axios.put(
        `${BASE_API_URL}/suppliers/${supplierId}/emailAgent/settings`,
        { aiAutomationSettings },
        { headers: await authHeaders() }
      );
      return response.data.data.aiAutomationSettings as AiAutomationSettings;
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to save settings'));
    }
  }
);
